// src/index.ts
import { ClientMsgCode, detectDupes } from "@liveblocks/core";
import { Base64 } from "js-base64";

// ../../node_modules/lib0/map.js
var create = () => /* @__PURE__ */ new Map();
var setIfUndefined = (map, key, createT) => {
  let set = map.get(key);
  if (set === void 0) {
    map.set(key, set = createT());
  }
  return set;
};

// ../../node_modules/lib0/set.js
var create2 = () => /* @__PURE__ */ new Set();

// ../../node_modules/lib0/array.js
var from = Array.from;
var isArray = Array.isArray;

// ../../node_modules/lib0/observable.js
var Observable = class {
  constructor() {
    this._observers = create();
  }
  /**
   * @param {N} name
   * @param {function} f
   */
  on(name, f) {
    setIfUndefined(this._observers, name, create2).add(f);
  }
  /**
   * @param {N} name
   * @param {function} f
   */
  once(name, f) {
    const _f = (...args) => {
      this.off(name, _f);
      f(...args);
    };
    this.on(name, _f);
  }
  /**
   * @param {N} name
   * @param {function} f
   */
  off(name, f) {
    const observers = this._observers.get(name);
    if (observers !== void 0) {
      observers.delete(f);
      if (observers.size === 0) {
        this._observers.delete(name);
      }
    }
  }
  /**
   * Emit a named event. All registered event listeners that listen to the
   * specified name will receive the event.
   *
   * @todo This should catch exceptions
   *
   * @param {N} name The event name.
   * @param {Array<any>} args The arguments that are applied to the event listener.
   */
  emit(name, args) {
    return from((this._observers.get(name) || create()).values()).forEach((f) => f(...args));
  }
  destroy() {
    this._observers = create();
  }
};

// src/index.ts
import * as Y from "yjs";

// src/version.ts
var PKG_NAME = "@liveblocks/yjs";
var PKG_VERSION = "1.5.3";
var PKG_FORMAT = "esm";

// src/index.ts
detectDupes(PKG_NAME, PKG_VERSION, PKG_FORMAT);
var Y_PRESENCE_KEY = "__yjs";
var Awareness = class extends Observable {
  constructor(doc, room) {
    super();
    this.states = /* @__PURE__ */ new Map();
    // Meta is used to keep track and timeout users who disconnect. Liveblocks provides this for us, so we don't need to
    // manage it here. Unfortunately, it's expected to exist by various integrations, so it's an empty map.
    this.meta = /* @__PURE__ */ new Map();
    // _checkInterval this would hold a timer to remove users, but Liveblock's presence already handles this
    // unfortunately it's typed by various integrations
    this._checkInterval = 0;
    this.doc = doc;
    this.room = room;
    this.clientID = doc.clientID;
    this.othersUnsub = this.room.events.others.subscribe((event) => {
      if (event.type === "leave") {
        this.emit("change", [
          { added: [], updated: [], removed: [event.user.connectionId] },
          "local"
        ]);
      }
      if (event.type === "enter") {
        this.emit("change", [
          { added: [event.user.connectionId], updated: [], removed: [] },
          "local"
        ]);
      }
      if (event.type === "update") {
        this.emit("change", [
          { added: [], updated: [event.user.connectionId], removed: [] },
          "local"
        ]);
      }
    });
  }
  destroy() {
    this.emit("destroy", [this]);
    this.othersUnsub();
    this.setLocalState(null);
    super.destroy();
  }
  getLocalState() {
    const presence = this.room.getPresence();
    if (Object.keys(presence).length === 0 || typeof presence[Y_PRESENCE_KEY] === "undefined") {
      return null;
    }
    return presence[Y_PRESENCE_KEY];
  }
  setLocalState(state) {
    const presence = this.room.getSelf()?.presence[Y_PRESENCE_KEY];
    this.room.updatePresence({
      __yjs: { ...presence || {}, ...state || {} }
    });
  }
  setLocalStateField(field, value) {
    const presence = this.room.getSelf()?.presence[Y_PRESENCE_KEY];
    const update = { [field]: value };
    this.room.updatePresence({
      __yjs: { ...presence || {}, ...update }
    });
  }
  // Translate liveblocks presence to yjs awareness
  getStates() {
    const others = this.room.getOthers();
    const states = others.reduce((acc, currentValue) => {
      if (currentValue.connectionId) {
        acc.set(
          currentValue.connectionId,
          currentValue.presence[Y_PRESENCE_KEY] || {}
        );
      }
      return acc;
    }, /* @__PURE__ */ new Map());
    return states;
  }
};
var LiveblocksProvider = class extends Observable {
  constructor(room, doc) {
    super();
    this.unsubscribers = [];
    this._synced = false;
    this.syncDoc = () => {
      this.synced = false;
      this.doc.clientID = this.room.getSelf()?.connectionId || this.doc.clientID;
      this.awareness.clientID = this.doc.clientID;
      const encodedVector = Base64.fromUint8Array(Y.encodeStateVector(this.doc));
      this.room.fetchYDoc(encodedVector);
    };
    this.updateHandler = (update, origin) => {
      if (origin !== "backend") {
        const encodedUpdate = Base64.fromUint8Array(update);
        this.room.updateYDoc(encodedUpdate);
      }
    };
    this.doc = doc;
    this.room = room;
    const connectionId = this.room.getSelf()?.connectionId;
    if (connectionId) {
      this.doc.clientID = connectionId;
    }
    this.awareness = new Awareness(this.doc, this.room);
    this.doc.on("update", this.updateHandler);
    this.unsubscribers.push(
      this.room.events.status.subscribe((status) => {
        if (status === "connected") {
          this.syncDoc();
        } else {
          this.synced = false;
        }
      })
    );
    this.unsubscribers.push(
      this.room.events.ydoc.subscribe((message) => {
        const { type, update } = message;
        if (type === ClientMsgCode.UPDATE_YDOC) {
          return;
        }
        Y.applyUpdate(this.doc, Base64.toUint8Array(update), "backend");
        const { stateVector } = message;
        if (stateVector) {
          try {
            const localUpdate = Y.encodeStateAsUpdate(
              this.doc,
              Base64.toUint8Array(stateVector)
            );
            this.room.updateYDoc(Base64.fromUint8Array(localUpdate));
          } catch (e) {
            console.warn(e);
          }
          this.synced = true;
        }
      })
    );
    this.syncDoc();
  }
  // The sync'd property is required by some provider implementations
  get synced() {
    return this._synced;
  }
  set synced(state) {
    if (this._synced !== state) {
      this._synced = state;
      this.emit("synced", [state]);
      this.emit("sync", [state]);
    }
  }
  destroy() {
    this.doc.off("update", this.updateHandler);
    this.unsubscribers.forEach((unsub) => unsub());
    this.awareness.destroy();
    super.destroy();
  }
  // Some provider implementations expect to be able to call connect/disconnect, implement as noop
  disconnect() {
  }
  connect() {
  }
};
export {
  Awareness,
  LiveblocksProvider as default
};
//# sourceMappingURL=index.mjs.map